import React, { useState } from "react";
import { Heart, Users, Eye, Handshake, Gift, Building, CreditCard, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import ContactFormDialog from "@/components/ContactFormDialog";
import { supabase } from "@/integrations/supabase/client";

const TIERS = [
  { id: "bronze", name: "Bronze", amount: 50000, color: "text-amber-600", benefits: ["Logo on event website", "Social media recognition", "Certificate of appreciation"] },
  { id: "silver", name: "Silver", amount: 150000, color: "text-slate-400", benefits: ["All Bronze benefits", "Logo on event materials", "2 complimentary tickets", "Mention during event"] },
  { id: "gold", name: "Gold", amount: 500000, color: "text-yellow-500", borderClass: "border-yellow-500/50", benefits: ["All Silver benefits", "Prominent logo placement", "5 complimentary tickets", "Speaking opportunity", "Booth or table space"] },
  { id: "platinum", name: "Platinum", amount: 1000000, color: "text-primary", borderClass: "border-primary/50", benefits: ["All Gold benefits", "Title sponsor recognition", "10 complimentary tickets", "Keynote mention", "VIP networking access", "Post-event feature"] },
];

const INDIVIDUAL_AMOUNTS = [2500, 5000, 10000, 25000]; // In cents

const Sponsors: React.FC = () => {
  const [contactOpen, setContactOpen] = useState(false);
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState("");
  const [individualAmount, setIndividualAmount] = useState("");
  const [loadingIndividual, setLoadingIndividual] = useState(false);

  const scrollToTiers = () => {
    document.getElementById('sponsor-tiers')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCheckout = async (amount: number, paymentType: "corporate" | "individual", tier?: string) => {
    const loadingKey = tier || "individual";
    if (paymentType === "corporate") {
      setLoadingTier(loadingKey);
    } else {
      setLoadingIndividual(true);
    }

    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          amount,
          paymentType,
          tier,
          successUrl: `${window.location.origin}/payment-success`,
          cancelUrl: `${window.location.origin}/payment-cancel`,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      console.error("Checkout error:", err);
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setLoadingTier(null);
      setLoadingIndividual(false);
    }
  };

  const handleCustomCheckout = async () => {
    const amount = parseFloat(customAmount);
    if (isNaN(amount) || amount < 100) {
      toast.error("Please enter an amount of at least $100");
      return;
    }
    await handleCheckout(Math.round(amount * 100), "corporate", "custom");
  };

  const handleIndividualCheckout = async (amount: number) => {
    await handleCheckout(amount, "individual");
  };

  const handleCustomIndividualCheckout = async () => {
    const amount = parseFloat(individualAmount);
    if (isNaN(amount) || amount < 1) {
      toast.error("Please enter an amount of at least $1");
      return;
    }
    await handleCheckout(Math.round(amount * 100), "individual");
  };

  const canonicalUrl = `https://buildwithchrist.ai/sponsors`;

  return (
    <div className="relative">
      <Helmet>
        <title>Sponsor BuildWithChrist.ai - Support Nonprofits Through AI</title>
        <meta name="description" content="Partner with BuildWithChrist.ai to support nonprofits and help resource AI tools built during our events. Connect with values-aligned talent and gain brand visibility." />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <ContactFormDialog 
        open={contactOpen} 
        onOpenChange={setContactOpen} 
        defaultReason="Sponsorship"
      />

      {/* Hero Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-primary/10 rounded-full blur-[100px]" />
        <div className="max-w-4xl mx-auto text-center relative z-10 space-y-6">
          <h1 className="text-4xl md:text-6xl font-bold leading-tight font-display">
            <span className="text-gradient">Partner With Purpose</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Support nonprofits and help resource the AI tools built during our events. Your sponsorship directly enables meaningful impact.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button size="lg" className="text-lg" onClick={scrollToTiers}>
              View Sponsorship Tiers
            </Button>
            <Button size="lg" variant="outline" className="text-lg" onClick={() => setContactOpen(true)}>
              Talk to Us
            </Button>
          </div>
        </div>
      </section>

      {/* Why Sponsor */}
      <section className="py-16 px-4 bg-card/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 font-display">Why Sponsor?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-card border border-border p-6 rounded-xl">
              <Heart className="text-secondary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Direct Nonprofit Impact</h3>
              <p className="text-sm text-muted-foreground">Your support funds the tools and resources that help nonprofits accelerate their missions.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <Eye className="text-primary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Brand Visibility</h3>
              <p className="text-sm text-muted-foreground">Get exposure before, during, and after the event through our marketing, venue branding, and digital presence.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <Users className="text-secondary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Values-Aligned Talent</h3>
              <p className="text-sm text-muted-foreground">Connect with mission-driven professionals, developers, and leaders who share your commitment to service.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <Handshake className="text-primary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Networking Access</h3>
              <p className="text-sm text-muted-foreground">Exclusive opportunities to engage with attendees, nonprofit partners, and other sponsors.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <Gift className="text-secondary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Corporate Giving</h3>
              <p className="text-sm text-muted-foreground">Align your corporate social responsibility with meaningful, measurable outcomes.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <Building className="text-primary mb-4" size={32} />
              <h3 className="font-bold text-lg mb-2">Community Leadership</h3>
              <p className="text-sm text-muted-foreground">Be recognized as a leader in supporting faith-based innovation and community service.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsorship Tiers */}
      <section id="sponsor-tiers" className="py-16 px-4 bg-primary/5 border-y border-primary/10">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 font-display">Corporate Sponsorship Tiers</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Choose the level of partnership that fits your organization. All sponsors receive recognition and our sincere gratitude.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TIERS.map((tier) => (
              <div key={tier.id} className={`bg-card border ${tier.borderClass || "border-border"} p-6 rounded-xl text-center flex flex-col`}>
                <h3 className={`font-bold text-xl mb-2 ${tier.color}`}>{tier.name}</h3>
                <p className="text-2xl font-bold mb-4">${(tier.amount / 100).toLocaleString()}</p>
                <ul className="text-sm text-muted-foreground text-left space-y-2 flex-grow">
                  {tier.benefits.map((benefit, i) => (
                    <li key={i}>• {benefit}</li>
                  ))}
                </ul>
                <Button 
                  className="w-full mt-6" 
                  onClick={() => handleCheckout(tier.amount, "corporate", tier.id)}
                  disabled={loadingTier !== null}
                >
                  {loadingTier === tier.id ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</>
                  ) : (
                    <><CreditCard className="mr-2 h-4 w-4" /> Sponsor Now</>
                  )}
                </Button>
              </div>
            ))}
          </div>

          {/* Custom Amount */}
          <div className="mt-8 bg-card border border-border p-6 rounded-xl max-w-md mx-auto">
            <h3 className="font-bold text-lg mb-4 text-center">Custom Sponsorship</h3>
            <p className="text-sm text-muted-foreground text-center mb-4">
              Want to contribute a different amount? Enter your custom sponsorship below.
            </p>
            <div className="flex gap-3">
              <div className="flex-1">
                <Label htmlFor="custom-amount" className="sr-only">Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="custom-amount"
                    type="number"
                    min="100"
                    step="1"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="500"
                    className="pl-7"
                  />
                </div>
              </div>
              <Button 
                onClick={handleCustomCheckout}
                disabled={loadingTier !== null}
              >
                {loadingTier === "custom" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Give Now"
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center mt-2">Minimum $100</p>
          </div>
        </div>
      </section>

      {/* Individual Supporter Section */}
      <section className="py-16 px-4 bg-secondary/5 border-y border-secondary/10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 font-display">Support as an Individual</h2>
          <p className="text-lg text-muted-foreground mb-8">
            Not representing a company? You can still make a difference. Every contribution helps us serve nonprofits.
          </p>
          
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {INDIVIDUAL_AMOUNTS.map((amount) => (
              <Button
                key={amount}
                variant="outline"
                size="lg"
                onClick={() => handleIndividualCheckout(amount)}
                disabled={loadingIndividual}
                className="min-w-[100px]"
              >
                ${(amount / 100).toLocaleString()}
              </Button>
            ))}
          </div>

          <div className="flex gap-3 max-w-xs mx-auto">
            <div className="flex-1">
              <Label htmlFor="individual-amount" className="sr-only">Custom Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="individual-amount"
                  type="number"
                  min="1"
                  step="1"
                  value={individualAmount}
                  onChange={(e) => setIndividualAmount(e.target.value)}
                  placeholder="Other amount"
                  className="pl-7"
                />
              </div>
            </div>
            <Button 
              onClick={handleCustomIndividualCheckout}
              disabled={loadingIndividual}
            >
              {loadingIndividual ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Give"
              )}
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold font-display">Have Questions?</h2>
          <p className="text-lg text-muted-foreground">
            Contact us to discuss sponsorship opportunities and how your organization can support our mission.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => setContactOpen(true)}>Contact Us</Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Sponsors;
